import React from 'react';
import { Link } from 'react-router-dom';
import { FaRegBuilding, FaChevronRight } from 'react-icons/fa';
import { RiMedicineBottleLine } from 'react-icons/ri';
import { classifyMatchingIngredients } from '../../utils/ingredientSearchUtils';

/**
 * Maximum number of ingredient tags to display before showing "+N more"
 */
const MAX_DISPLAYED_INGREDIENTS = 4;

/**
 * Card component displaying a matching product in search results.
 * Shows product name, manufacturer, and ingredient tags with highlighting
 * for ingredients that match the user's selection.
 * 
 * @param {Object} props
 * @param {Object} props.product - Product data
 * @param {string} props.product.uri - Product URI for navigation
 * @param {string} props.product.name - Product name
 * @param {Object} props.product.manufacturer - Manufacturer object with name
 * @param {Array<{uri: string, name: string}>} props.product.ingredients - Product ingredients
 * @param {string[]} props.selectedIngredientUris - URIs of user-selected ingredients for highlighting
 */
export default function ProductResultCard({
  product,
  selectedIngredientUris = [],
}) {
  // Classify which ingredients match the user's selection
  const classifiedIngredients = classifyMatchingIngredients(
    product.ingredients || [],
    selectedIngredientUris
  );

  // Determine how many ingredients to display and if we need a "+N more" indicator
  const displayedIngredients = classifiedIngredients.slice(0, MAX_DISPLAYED_INGREDIENTS);
  const remainingCount = classifiedIngredients.length - MAX_DISPLAYED_INGREDIENTS;
  const hasMore = remainingCount > 0;

  return (
    <Link
      to={`/tradenames/${product.uri}`}
      className="block overflow-hidden transition-all duration-200 border bg-white/40 backdrop-blur-sm border-white/60 group rounded-xl hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-emerald-600/30"
    >
      <div className="flex p-4">
        {/* Product Icon */}
        <div className="flex-shrink-0 mr-3">
          <div className="flex items-center justify-center transition-colors rounded-full w-10 h-10 bg-emerald-300/20 text-emerald-600 group-hover:bg-emerald-100">
            <RiMedicineBottleLine className="size-5" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Product Name */}
          <div className="font-medium transition-colors text-16 text-secondary group-hover:text-emerald-700 leading-snug">
            {product.name?.replace('&amp;', '&') || 'Unknown Product'}
          </div>

          {/* Manufacturer Name */}
          <div className="flex items-center gap-2 mt-1.5 text-14 text-secondary/70">
            <FaRegBuilding className="min-w-4 max-w-4 text-secondary/60" />
            <span className="w-full truncate transition-colors font-text">
              {product.manufacturers?.map(m => m.name).join(', ') || product.manufacturer?.name || 'Unknown Manufacturer'}
            </span>
          </div>

          {/* Ingredient Tags */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {displayedIngredients.map((ingredient) => (
              <span
                key={ingredient.uri}
                className={`px-2.5 py-1 text-12 font-text rounded-full ${
                  ingredient.isMatch
                    ? 'bg-emerald-600/15 border border-emerald-600/30 text-emerald-700'
                    : 'bg-secondary/5 text-secondary/70'
                }`}
              >
                {ingredient.name?.replace('&amp;', '&') || 'Unknown'}
              </span>
            ))}
            {hasMore && (
              <span className="px-2.5 py-1 text-12 font-text rounded-full bg-secondary/5 text-secondary/60">
                +{remainingCount} more
              </span>
            )}
          </div>
        </div>

        {/* Chevron Arrow */}
        <div className="self-center ml-2 -mr-1 transition-transform transform group-hover:translate-x-1">
          <div className="p-1 rounded-full text-secondary/40 group-hover:text-emerald-600">
            <FaChevronRight className="size-3" />
          </div>
        </div>
      </div>
    </Link>
  );
}
